import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListSmsResult } from '@whinerp/client';
import { formatCount, formatMoney } from '@whinerp/contracts/format';
import { SMS_PRICE_CENTS, smsSegments } from '@whinerp/contracts/sms';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * SMS (F-070): the composer counts characters and segments and estimates the
 * cost as it is typed - "SMS cost surprises are a common complaint" - naming
 * the character that turns a message into the expensive encoding. DLT status
 * is shown on every template.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

export function SmsPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-070');
  const { can } = useSession();
  const data = useQuery<ListSmsResult, ApiError>({ queryKey: ['sms'], queryFn: () => api.listSms({}), enabled: enabled.enabled });
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [country, setCountry] = useState('AE');
  const [recipients, setRecipients] = useState('1000');
  const [dltId, setDltId] = useState('');
  const [dltStatus, setDltStatus] = useState<'not-needed' | 'pending' | 'approved' | 'rejected'>('not-needed');
  const save = useMutation({
    mutationFn: () => api.saveSmsTemplate({ body: { templateId: null, name, body, dltTemplateId: dltId || null, dltStatus } }),
    onSuccess: async () => {
      setName('');
      setBody('');
      await queryClient.invalidateQueries({ queryKey: ['sms'] });
    },
  });
  if (!enabled.enabled) return null;
  const count = smsSegments(body);
  const people = Number.parseInt(recipients, 10) || 0;
  const cents = (SMS_PRICE_CENTS[country] ?? SMS_PRICE_CENTS['DEFAULT']!) * count.segments * people;
  return (
    <Panel data-region="sms">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('sms.title')}</SectionLabel>
        <ul className="flex flex-col">
          {(data.data?.templates ?? []).map((template) => (
            <li key={template.templateId} data-sms-template={template.name} className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0">
              <span className="text-dense text-ink">{template.name}</span>
              <span className="text-meta text-ink-meta">{t('sms.segmentsShort', { values: { count: String(template.segments), encoding: template.encoding } })}</span>
              <span data-dlt-status={template.dltStatus} className={template.dltStatus === 'approved' || template.dltStatus === 'not-needed' ? 'text-meta text-ink-meta' : 'text-meta text-warning-text'}>
                {t(`sms.dlt.${template.dltStatus}`)}
              </span>
            </li>
          ))}
        </ul>
        {can('campaign.send') ? (
          <div className="flex flex-col gap-2 rounded-card border border-rule p-2">
            <input data-sms-name aria-label={t('sms.name')} placeholder={t('sms.name')} className={INPUT} value={name} onChange={(e) => setName(e.target.value)} />
            <textarea data-sms-body rows={3} aria-label={t('sms.body')} placeholder={t('sms.bodyHint')} className="rounded-control border border-control bg-surface px-3 py-2 text-body text-ink" value={body} onChange={(e) => setBody(e.target.value)} />
            <p data-sms-counter className="text-meta text-ink">
              {t('sms.counter', { values: { chars: formatCount(count.characters, format), segments: formatCount(count.segments, format), encoding: count.encoding, left: formatCount(count.remaining, format) } })}
            </p>
            {count.unicodeBecause ? <p data-sms-unicode className="text-meta text-warning-text">{t('sms.unicode', { values: { char: count.unicodeBecause } })}</p> : null}
            <div className="flex flex-wrap items-center gap-2 text-meta text-ink-600">
              <label className="flex items-center gap-1">
                {t('sms.to')}
                <input data-sms-recipients inputMode="numeric" className={`${INPUT} w-24`} value={recipients} onChange={(e) => setRecipients(e.target.value)} />
              </label>
              <select data-sms-country aria-label={t('sms.country')} className={INPUT} value={country} onChange={(e) => setCountry(e.target.value)}>
                {Object.keys(SMS_PRICE_CENTS).filter((key) => key !== 'DEFAULT').map((key) => (
                  <option key={key} value={key}>{key}</option>
                ))}
              </select>
              <span data-sms-cost>{t('sms.cost', { values: { amount: formatMoney(String(Math.round(cents) / 100), format, { currency: 'USD' }) } })}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select data-sms-dlt aria-label={t('sms.dltLabel')} className={INPUT} value={dltStatus} onChange={(e) => setDltStatus(e.target.value as typeof dltStatus)}>
                {(['not-needed', 'pending', 'approved', 'rejected'] as const).map((status) => (
                  <option key={status} value={status}>{t(`sms.dlt.${status}`)}</option>
                ))}
              </select>
              {dltStatus !== 'not-needed' ? <input data-sms-dlt-id aria-label={t('sms.dltId')} placeholder={t('sms.dltId')} className={INPUT} value={dltId} onChange={(e) => setDltId(e.target.value)} /> : null}
              <Button intent="primary" size="small" data-sms-save disabled={save.isPending} onClick={() => save.mutate()}>
                {t('sms.save')}
              </Button>
            </div>
            {save.error ? <p data-sms-refusal className="text-dense text-danger-text">{errorText(save.error)}</p> : null}
          </div>
        ) : null}
      </div>
    </Panel>
  );
}
